import { ApiDataItemDto, TestParamDto } from '@/Domain/api/Action/dto/Action.dto';
import { HttpActionForm } from '@/Domain/hooks/Action/ApiAction/useHttpRequestActionFormV2.hook';

const PARAMETER_PATTERN = /\{\{([^}]+)\}\}/g;

export const HttpActionHelper = {
  injectParameterIdsForUrl: (
    url: string = '',
    parameters: HttpActionForm['parameters'] = [],
  ): string =>
    url.replace(PARAMETER_PATTERN, (match, paramName: string) => {
      const matchedParam = parameters.find((p) => p.name.replaceAll(' ', '_') === paramName);
      return matchedParam ? `{{${matchedParam.id}}}` : match;
    }),

  replaceParameterIdsWithNamesForUrl: (
    url: string,
    parameters: HttpActionForm['parameters'] = [],
  ): string =>
    url.replace(PARAMETER_PATTERN, (match, paramId) => {
      const matchedParam = parameters.find((p) => String(p.id) === paramId);
      return matchedParam ? `{{${matchedParam.name.replaceAll(' ', '_')}}}` : match;
    }),

  replaceParameterIdsWithNames: (
    items: ApiDataItemDto[],
    parameters: HttpActionForm['parameters'] = [],
  ) =>
    items.map((item) => {
      const replaceIds = (str: string): string =>
        str.replace(PARAMETER_PATTERN, (match, paramId) => {
          const matchedParam = parameters.find((p) => String(p.id) === paramId);
          return matchedParam ? `{{${matchedParam.name.replaceAll(' ', '_')}}}` : match;
        });

      return {
        ...item,
        name: replaceIds(item.name),
        ...(item.value !== undefined
          ? { value: replaceIds(item.value) }
          : { content: replaceIds(item.content!) }),
      };
    }),

  injectParameterIds: (
    items: ApiDataItemDto[] | TestParamDto[],
    parameters: HttpActionForm['parameters'] = [],
  ) =>
    items.map((item) => {
      const replacePlaceholders = (str: string): string =>
        str.replace(PARAMETER_PATTERN, (match, paramName: string) => {
          const matchedParam = parameters.find((p) => p.name.replaceAll(' ', '_') === paramName);
          return matchedParam ? `{{${matchedParam.id}}}` : match;
        });

      return {
        ...item,
        name: replacePlaceholders(item.name),
        ...(item.value !== undefined
          ? { value: replacePlaceholders(item.value.toString()) }
          : { content: replacePlaceholders(item.content!) }),
      };
    }),

  injectParameterIdsInString: (
    input: string,
    parameters: HttpActionForm['parameters'] = [],
  ): string => {
    if (!input) return input;

    const byNameUnderscored = new Map<string, number | string>(
      parameters.map((p) => [String(p.name).replaceAll(' ', '_'), p.id]),
    );

    return input.replace(PARAMETER_PATTERN, (match: string, rawName: string) => {
      const key = rawName.trim().replace(/\s+/g, '_');
      const id = byNameUnderscored.get(key);
      return id != null ? `{{${id}}}` : match;
    });
  },

  replaceParameterIdsWithNamesInString: (
    input: string,
    parameters: HttpActionForm['parameters'] = [],
  ): string => {
    if (!input) return input;

    const idToUnderscoredName = new Map<string, string>(
      parameters.map((p) => [String(p.id), String(p.name).replace(/\s+/g, '_')]),
    );

    return input.replace(PARAMETER_PATTERN, (match: string, rawId: string) => {
      const idKey = String(rawId).trim();
      const underscored = idToUnderscoredName.get(idKey);
      return underscored != null ? `{{${underscored}}}` : match;
    });
  },
};
